import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FaLock } from 'react-icons/fa';

/**
 * Highlighted callout summarising trial/pricing terms and guarantees.
 * Supports both trial (with trialDays) and no-trial (trialDays=0) variants.
 *
 * Visual hierarchy:
 * - Line 1: Primary offer (trial info or price) - prominent
 * - Line 2: Monthly equivalent (when annual billing) - secondary emphasis
 * - Line 3: Reassurances (cancel anytime, guarantee) - tertiary
 *
 * NOTE: Uses same lock icon for all variants to ensure A/B test validity.
 * Only the text content should differ between variants.
 */
export default function CheckoutTrialCallout({
  trialDays,
  priceText,
  annualMonthlyEquivalent,
  guaranteeText = '30-day money-back guarantee',
  className = '',
}) {
  const hasTrial = trialDays > 0;

  return (
    <div className={classNames('trial-callout', className)}>
      <div className="callout-content">
        <div className="callout-primary">
          {hasTrial ? (
            <>
              <strong>{trialDays}-day FREE trial</strong>
              <span className="callout-separator">•</span>
              <span>Then {priceText}</span>
            </>
          ) : (
            <strong className="callout-price">{priceText}</strong>
          )}
        </div>
        {/* Show monthly equivalent when annual billing is selected */}
        {annualMonthlyEquivalent && (
          <div className="callout-monthly-equivalent">
            (Just {annualMonthlyEquivalent})
          </div>
        )}
        <div className="callout-secondary">
          <FaLock className="callout-icon" />
          Cancel anytime
          <span className="callout-separator">•</span>
          {guaranteeText}
        </div>
      </div>
    </div>
  );
}

CheckoutTrialCallout.propTypes = {
  trialDays: PropTypes.number.isRequired,
  priceText: PropTypes.string.isRequired,
  annualMonthlyEquivalent: PropTypes.string,
  guaranteeText: PropTypes.string,
  className: PropTypes.string,
};
